import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "438",
  "slug": "what-is-el-nino",
  "title": "What Is El Niño?",
  "excerpt": "Every few years a patch of the Pacific Ocean warms up and reshuffles weather across the entire planet. Here is what El Niño is, why it happens, and how it affects you.",
  "date": "2026-06-14",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1675720998093-f6dd91cb9016?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxwYWNpZmljJTIwb2NlYW4lMjB3YXZlc3xlbnwxfDB8fHwxNzgxNDI1MDc5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "El Niño is the warm phase of a recurring climate pattern in the tropical Pacific Ocean. Every few years, weakened trade winds let the central and eastern Pacific warm up, which shifts rainfall and temperature patterns around the world.",
  "content": [
    {
      "type": "paragraph",
      "text": "It sounds almost too strange to be true: a change in ocean temperature in one stretch of the tropical Pacific can mean droughts in Australia, floods in Peru, a quieter Atlantic hurricane season, and a warmer winter in parts of North America, all in the same year. That far-reaching reshuffle is El Niño, one of the most important climate patterns on the planet."
    },
    {
      "type": "heading",
      "text": "The normal state of the Pacific"
    },
    {
      "type": "paragraph",
      "text": "In a typical year, steady trade winds blow from east to west across the tropical Pacific, pushing warm surface water toward Asia and Australia. As that warm water piles up in the west, cooler water wells up from the deep along the coast of South America. This setup brings heavy rain to the western Pacific and drier, cooler conditions to the eastern side."
    },
    {
      "type": "heading",
      "text": "What happens during El Niño"
    },
    {
      "type": "paragraph",
      "text": "Every two to seven years, those trade winds weaken or even reverse. Without the winds holding it back, the warm pool of water slides eastward across the Pacific. The result is unusually warm surface water in the central and eastern Pacific, and a corresponding shift in where the heaviest rains fall. Because the atmosphere and ocean are tightly linked, that shift ripples outward and rearranges weather patterns far beyond the Pacific."
    },
    {
      "type": "paragraph",
      "text": "El Niño is the warm half of a larger cycle that scientists call the El Niño Southern Oscillation. Its cool counterpart, La Niña, is essentially the opposite: stronger trade winds and cooler-than-usual water in the eastern Pacific. Between them sits a neutral phase."
    },
    {
      "type": "heading",
      "text": "How it affects the world"
    },
    {
      "type": "list",
      "items": [
        "Wetter conditions and flooding risk in parts of South America and the southern United States.",
        "Drought and elevated wildfire risk in Indonesia, Australia, and Southeast Asia.",
        "A tendency to suppress Atlantic hurricane activity while boosting storms in the Pacific.",
        "Disruption to fisheries off South America, where the warm water cuts off the nutrient-rich upwelling that fish depend on.",
        "A small nudge upward in global average temperatures during strong events."
      ]
    },
    {
      "type": "heading",
      "text": "El Niño versus La Niña"
    },
    {
      "type": "paragraph",
      "text": "It helps to think of the tropical Pacific as having three moods rather than two. The neutral phase is the in-between state, when winds and temperatures are close to average. El Niño is the warm, restless phase that scatters the usual patterns. La Niña is the cool, intensified phase, with stronger-than-normal trade winds piling even more warm water into the western Pacific. The two phases often push weather in opposite directions, so a region that floods during one may face drought during the other."
    },
    {
      "type": "paragraph",
      "text": "Some events are far stronger than others. The major El Niño of 1997 and 1998, and another strong event in 2015 and 2016, ranked among the most powerful on record and were linked to unusual weather across multiple continents. Crucially, El Niño is a natural cycle, not the same thing as long-term climate change, though scientists study how a warming world may influence the strength and frequency of these swings."
    },
    {
      "type": "heading",
      "text": "Where the name comes from"
    },
    {
      "type": "paragraph",
      "text": "The name dates back to South American fishermen, who noticed that the warm water tended to arrive around Christmas. They called it El Niño, Spanish for the boy, a reference to the Christ child. Centuries later, scientists adopted the term for the full ocean and atmosphere phenomenon."
    },
    {
      "type": "heading",
      "text": "How we watch it"
    },
    {
      "type": "paragraph",
      "text": "Today El Niño is monitored with a network of ocean buoys, ships, and satellites that track sea surface temperatures across the Pacific in near real time. That constant watch lets forecasters give months of warning, helping farmers, water managers, and emergency planners prepare for the season ahead."
    },
    {
      "type": "paragraph",
      "text": "One thing El Niño is not is a forecast for any single day. It tilts the odds of a season being wetter or drier, warmer or cooler, but it does not guarantee a specific outcome in any one place. Two El Niño years can play out quite differently, because local geography and other weather patterns always have a say. Forecasters treat it as one powerful ingredient in the mix, not a switch that decides everything."
    },
    {
      "type": "paragraph",
      "text": "El Niño is a reminder that the planet is one connected system, where the ocean and atmosphere are always in conversation. If you enjoy seeing how Earth fits together, EarthGuessr lets you explore its landscapes and climates from above, one satellite view at a time."
    }
  ]
};
